import { z } from "zod";
import { narrativeBookProductSchema } from "./narrative-book-production.js";
export const narrativeMediaTruthClassSchema = z.enum([
    "DOCUMENTARY_ORIGINAL",
    "DOCUMENTARY_RESTORATION",
    "SYNTHETIC_FACSIMILE",
    "EDITORIAL_ILLUSTRATION",
    "TYPOGRAPHIC_OR_DIAGRAMMATIC",
]);
export const narrativeMediaApprovalSchema = z.enum([
    "SYNTHETIC_FIXTURE_ONLY",
    "AWAITING_OWNER_APPROVAL",
    "AWAITING_DEPICTED_PERSON_APPROVAL",
    "AWAITING_JOINT_APPROVAL",
    "APPROVED_FOR_BOOK",
    "EXCLUDED",
]);
export const narrativePageArchetypeSchema = z.enum([
    "COVER_EMBLEM",
    "QUIET_FRONTMATTER",
    "TIMELINE",
    "CHAPTER_THRESHOLD",
    "LITERARY_PROSE",
    "PAIRED_RECOLLECTION",
    "ARCHIVE_FACSIMILE",
    "OBJECT_WITH_TWO_CAPTIONS",
    "LEGACY_LETTER",
    "REFLECTION",
    "ENDMATTER",
    "INTENTIONAL_BLANK",
]);
export const narrativeImageOperationRouteSchema = z.enum([
    "USE_APPROVED_ORIGINAL",
    "BOUNDED_RESTORATION_EDIT",
    "GENERATE_EDITORIAL_ILLUSTRATION",
    "DETERMINISTIC_RENDERER",
    "OMIT",
]);
export const narrativeVisualAssetSchema = z
    .object({
    assetId: z.string().trim().min(1).max(200),
    chapterId: z.string().trim().min(1).max(200).optional(),
    title: z.string().trim().min(1).max(200),
    truthClass: narrativeMediaTruthClassSchema,
    approval: narrativeMediaApprovalSchema,
    sourceIds: z.array(z.string().trim().min(1).max(200)).max(50),
    generationAllowed: z.boolean(),
    depictsIdentifiablePeople: z.boolean(),
    proofOnly: z.boolean(),
    customerDisclosure: z.string().trim().min(1).max(500),
    replacementRequirement: z.string().trim().min(1).max(500).optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (["DOCUMENTARY_ORIGINAL", "DOCUMENTARY_RESTORATION"].includes(value.truthClass) &&
        value.sourceIds.length === 0)
        context.addIssue({
            code: "custom",
            path: ["sourceIds"],
            message: "Documentary media requires at least one immutable source asset.",
        });
    if (value.truthClass === "DOCUMENTARY_ORIGINAL" && value.generationAllowed)
        context.addIssue({
            code: "custom",
            path: ["generationAllowed"],
            message: "An original documentary asset can never be image-generated.",
        });
    if (value.truthClass === "SYNTHETIC_FACSIMILE" && !value.proofOnly)
        context.addIssue({
            code: "custom",
            path: ["proofOnly"],
            message: "Synthetic facsimiles are proof-only and cannot ship as documentary media.",
        });
    if (value.depictsIdentifiablePeople && value.truthClass === "SYNTHETIC_FACSIMILE")
        context.addIssue({
            code: "custom",
            path: ["depictsIdentifiablePeople"],
            message: "Synthetic facsimiles may not invent identifiable family likenesses.",
        });
});
export const narrativeVisualProductionPlanSchema = z
    .object({
    contract: z.literal("narrative-visual-production/1"),
    strategyVersion: z.literal("TRUTHFUL_MEMOIR_VISUAL_PIPELINE_V1"),
    product: narrativeBookProductSchema,
    benchmarkMethod: z.literal("PAGE_AWARE_PLAN_GENERATE_CRITIQUE_REPAIR"),
    modelRoles: z
        .object({
        narrativeModel: z.literal("evidence allocation, page architecture, visual-opportunity planning, caption drafting, and critique"),
        imageModel: z.literal("approved editorial artwork or bounded restoration only; never facts, prose, documentary evidence, or unapproved likenesses"),
        renderer: z.literal("deterministic live typography, captions, facsimiles, trim geometry, and final PDF composition"),
    })
        .strict(),
    stages: z
        .tuple([
        z.literal("EVIDENCE_AND_CONSENT_GATE"),
        z.literal("WHOLE_BOOK_VISUAL_ARCHITECTURE"),
        z.literal("ASSET_INVENTORY_AND_GAP_PLAN"),
        z.literal("PAGE_AWARE_ART_DIRECTION"),
        z.literal("REPRESENTATIVE_PROOFS"),
        z.literal("OWNER_AND_EDITOR_REVIEW"),
        z.literal("BOUNDED_PRODUCTION"),
        z.literal("SEQUENCE_CRITIQUE_AND_REPAIR"),
        z.literal("RENDER_PREFLIGHT_AND_PHYSICAL_PROOF"),
    ])
        .readonly(),
    assets: z.array(narrativeVisualAssetSchema).min(1),
    qualityTargets: z
        .object({
        maximumConsecutiveLiteraryProsePages: z.number().int().min(2).max(6),
        minimumPageArchetypes: z.number().int().min(5).max(12),
        minimumPalettePhases: z.number().int().min(2).max(6),
        minimumApprovedDocumentaryMedia: z.number().int().nonnegative(),
        typographyRenderedByImageModel: z.literal(false),
        generatedDocumentaryEvidenceAllowed: z.literal(false),
        visualMeaningMayDependOnColorAlone: z.literal(false),
    })
        .strict(),
    providerPolicy: z
        .object({
        fixtureModeProviderCalls: z.literal(0),
        paidCallsRequireExplicitAuthorization: z.literal(true),
        candidateLimitPerAsset: z.number().int().min(1).max(4),
        repairLimitPerAsset: z.number().int().min(0).max(2),
        promptAndReferenceChecksumsRequired: z.literal(true),
        costLedgerRequired: z.literal(true),
    })
        .strict(),
})
    .strict();
export const narrativeImageOperationSchema = z
    .object({
    assetId: z.string().trim().min(1).max(200),
    truthClass: narrativeMediaTruthClassSchema,
    route: narrativeImageOperationRouteSchema,
    providerPurpose: z.literal("EDITORIAL_KEEPSAKE").nullable(),
    providerCallAllowed: z.boolean(),
    paidCallAuthorized: z.literal(false),
    referenceSourceIds: z.array(z.string().trim().min(1).max(200)).max(50),
    liveTypographyRequired: z.literal(true),
    ownerReviewRequired: z.literal(true),
    promptConstraints: z.array(z.string().trim().min(1).max(500)).min(3).max(12),
})
    .strict()
    .superRefine((value, context) => {
    const providerRoutes = ["BOUNDED_RESTORATION_EDIT", "GENERATE_EDITORIAL_ILLUSTRATION"];
    if (providerRoutes.includes(value.route) !== value.providerCallAllowed)
        context.addIssue({
            code: "custom",
            path: ["providerCallAllowed"],
            message: "Only bounded restoration and editorial illustration routes may call an image provider.",
        });
    if (value.route === "BOUNDED_RESTORATION_EDIT" && value.referenceSourceIds.length === 0)
        context.addIssue({
            code: "custom",
            path: ["referenceSourceIds"],
            message: "A restoration edit requires an immutable owner-provided reference.",
        });
    if (value.truthClass === "DOCUMENTARY_ORIGINAL" &&
        value.route !== "USE_APPROVED_ORIGINAL" &&
        value.route !== "OMIT")
        context.addIssue({
            code: "custom",
            path: ["route"],
            message: "Documentary originals may only be used unchanged or omitted.",
        });
    if (value.truthClass === "SYNTHETIC_FACSIMILE" &&
        value.route !== "DETERMINISTIC_RENDERER" &&
        value.route !== "OMIT")
        context.addIssue({
            code: "custom",
            path: ["route"],
            message: "Proof-only facsimiles must be produced by the deterministic renderer or omitted.",
        });
});
export const narrativeImageOperationPlanSchema = z
    .object({
    contract: z.literal("narrative-image-operation-plan/1"),
    product: narrativeBookProductSchema,
    mode: z.literal("FIXTURE_NO_PROVIDER"),
    providerCallCount: z.literal(0),
    paidProviderCallCount: z.literal(0),
    operations: z.array(narrativeImageOperationSchema).min(1),
    sequenceMethod: z.literal("whole-book plan, representative proofs, owner review, bounded production, sequence critique, scoped repair"),
})
    .strict()
    .superRefine((value, context) => {
    if (value.operations.some((operation) => operation.providerCallAllowed))
        context.addIssue({
            code: "custom",
            path: ["operations"],
            message: "Fixture mode cannot contain an image-provider operation.",
        });
});
export const narrativeVisualLayoutEvidenceSchema = z
    .object({
    contract: z.literal("narrative-visual-layout-evidence/1"),
    product: narrativeBookProductSchema,
    pageArchetypes: z.array(narrativePageArchetypeSchema).min(1),
    artifactPages: z.array(z
        .object({
        interiorPage: z.number().int().positive(),
        assetId: z.string().trim().min(1).max(200),
        truthClass: narrativeMediaTruthClassSchema,
        proofOnly: z.boolean(),
    })
        .strict()),
    maximumObservedConsecutiveLiteraryProsePages: z.number().int().nonnegative(),
    distinctPageArchetypes: z.number().int().positive(),
    palettePhases: z.array(z.string().trim().min(1).max(80)).min(1),
    actualPaletteTokenIds: z.array(z.string().trim().min(1).max(120)).min(1),
    renderedBodyMasterIds: z.array(z.string().trim().min(1).max(120)).min(1),
    executedChapterFamilies: z.array(z.string().trim().min(1).max(120)).max(20),
    spreadCompositions: z
        .array(z
        .object({
        spreadId: z.string().trim().min(1).max(160),
        masterId: z.string().trim().min(1).max(120),
        pageNumbers: z.tuple([z.number().int().positive(), z.number().int().positive()]),
        semanticRoles: z.array(z.string().trim().min(1).max(120)).min(2).max(8),
        distinctFacingPageRoles: z.boolean(),
        parallelVoiceLayout: z.boolean(),
        sourceBound: z.boolean(),
    })
        .strict())
        .max(100),
    visiblePlaceholderLabels: z.array(z.string().trim().min(1).max(240)).max(50),
    generatedDocumentaryAssets: z.array(z.string().trim().min(1).max(200)).max(50),
    humanVisualReviewStatus: z.enum(["PENDING", "APPROVED", "REJECTED"]),
})
    .strict()
    .superRefine((value, context) => {
    if (new Set(value.pageArchetypes).size !== value.distinctPageArchetypes)
        context.addIssue({
            code: "custom",
            path: ["distinctPageArchetypes"],
            message: "Distinct archetype count must match the rendered sequence.",
        });
});
export const narrativeGoldenSpreadFamilySchema = z.enum([
    "MY_STORY_CHAPTER_THRESHOLD",
    "MY_STORY_PROSE_AND_DOCUMENT",
    "MY_STORY_OBJECT_AND_LEGACY",
    "OUR_STORY_INDEPENDENT_FACING_VOICES",
    "OUR_STORY_DIFFERING_RECOLLECTIONS",
    "OUR_STORY_JOINTLY_APPROVED_SHARED_REFLECTION",
]);
export const narrativeSpreadVoiceModeSchema = z.enum([
    "NELL_FIRST_PERSON",
    "MAYA_FIRST_PERSON",
    "ROWAN_FIRST_PERSON",
    "SHARED_WE",
    "EDITORIAL_CONTEXT",
]);
export const narrativeRepresentativeSpreadIntentSchema = z
    .object({
    contract: z.literal("narrative-spread-intent/1"),
    spreadId: z.string().trim().min(1).max(160),
    product: narrativeBookProductSchema,
    family: narrativeGoldenSpreadFamilySchema,
    purpose: z.string().trim().min(20).max(600),
    sourceParagraphIds: z.array(z.string().trim().min(1).max(200)).min(1).max(20),
    sourceAssetIds: z.array(z.string().trim().min(1).max(200)).max(10),
    voiceModes: z.array(narrativeSpreadVoiceModeSchema).min(1).max(5),
    paletteTokenIds: z.array(z.string().trim().min(1).max(120)).min(2).max(10),
    liveTypographyRequired: z.literal(true),
    generatedPixelsAllowed: z.literal(false),
    documentaryTruthRequired: z.literal(true),
    colorMayBeSoleMeaning: z.literal(false),
    forbiddenVisibleTerms: z.array(z.string().trim().min(1).max(120)).min(3).max(20),
})
    .strict()
    .superRefine((value, context) => {
    const familyMatches = (value.product === "MY_STORY" && value.family.startsWith("MY_STORY_")) ||
        (value.product === "OUR_STORY" && value.family.startsWith("OUR_STORY_"));
    if (!familyMatches)
        context.addIssue({
            code: "custom",
            path: ["family"],
            message: "Spread family must belong to the selected product.",
        });
    if (new Set(value.sourceParagraphIds).size !== value.sourceParagraphIds.length)
        context.addIssue({
            code: "custom",
            path: ["sourceParagraphIds"],
            message: "A representative spread cannot bind the same paragraph twice.",
        });
});
export const narrativeRenderedRegionSchema = z
    .object({
    regionId: z.string().trim().min(1).max(160),
    role: z.enum([
        "DISPLAY_TITLE",
        "BODY_PROSE",
        "VOICE_PROSE",
        "PULL_QUOTE",
        "DOCUMENT_FACSIMILE",
        "OBJECT_PORTRAIT",
        "CAPTION",
        "TIMELINE",
        "ATTRIBUTION",
    ]),
    pageSide: z.enum(["VERSO", "RECTO", "ACROSS_SPREAD"]),
    x: z.number().nonnegative(),
    y: z.number().nonnegative(),
    width: z.number().positive(),
    height: z.number().positive(),
    sourceIds: z.array(z.string().trim().min(1).max(200)).max(20),
    textCharacters: z.number().int().nonnegative(),
    minimumTextPoints: z.number().positive().nullable(),
})
    .strict();
export const narrativeRenderedSpreadEvidenceSchema = z
    .object({
    contract: z.literal("narrative-rendered-spread-evidence/1"),
    spreadId: z.string().trim().min(1).max(160),
    product: narrativeBookProductSchema,
    family: narrativeGoldenSpreadFamilySchema,
    pdfPageIndex: z.number().int().nonnegative(),
    masterId: z.string().trim().min(1).max(160),
    spreadWidthPoints: z.number().positive(),
    spreadHeightPoints: z.number().positive(),
    sourceParagraphIds: z.array(z.string().trim().min(1).max(200)).min(1).max(20),
    sourceAssetIds: z.array(z.string().trim().min(1).max(200)).max(10),
    paletteTokenIds: z.array(z.string().trim().min(1).max(120)).min(2).max(10),
    regions: z.array(narrativeRenderedRegionSchema).min(3).max(30),
    visibleTextChecksumSha256: z.string().regex(/^[a-f0-9]{64}$/u),
    maximumLineCharacters: z.number().int().positive().max(100),
    minimumTextPoints: z.number().positive(),
    overflowDetected: z.boolean(),
    liveTypography: z.boolean(),
    generatedPixels: z.boolean(),
    colorOnlyMeaning: z.boolean(),
    distinctFacingPageRoles: z.boolean(),
    customerVisibleTechnicalTerms: z.array(z.string().trim().min(1).max(120)).max(20),
})
    .strict();
export const narrativeRepresentativeSpreadProofSchema = z
    .object({
    contract: z.literal("narrative-representative-spread-proof/1"),
    product: narrativeBookProductSchema,
    title: z.string().trim().min(1).max(200),
    proofOnly: z.literal(true),
    generationMode: z.literal("fixture"),
    providerCallCount: z.literal(0),
    paidProviderCallCount: z.literal(0),
    trimWidthPoints: z.number().positive(),
    trimHeightPoints: z.number().positive(),
    spreadWidthPoints: z.number().positive(),
    spreadHeightPoints: z.number().positive(),
    pdfFilename: z.string().trim().min(1).max(240),
    pdfChecksumSha256: z.string().regex(/^[a-f0-9]{64}$/u),
    literalExtractOfCompleteBook: z.boolean(),
    sourcePdfChecksumSha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/u)
        .nullable(),
    sourcePdfPagePairs: z
        .array(z.tuple([z.number().int().nonnegative(), z.number().int().nonnegative()]))
        .length(3),
    pageCount: z.literal(3),
    intents: z.array(narrativeRepresentativeSpreadIntentSchema).length(3),
    renderedSpreads: z.array(narrativeRenderedSpreadEvidenceSchema).length(3),
    compositionStatus: z.enum(["PASS", "FAIL"]),
    customerReady: z.literal(false),
    blockers: z.array(z.string().trim().min(1).max(300)).min(1).max(20),
})
    .strict()
    .superRefine((value, context) => {
    if (value.literalExtractOfCompleteBook && value.sourcePdfChecksumSha256 === null)
        context.addIssue({
            code: "custom",
            path: ["sourcePdfChecksumSha256"],
            message: "A literal representative extract must name the complete-book checksum.",
        });
    if (!value.literalExtractOfCompleteBook && value.sourcePdfChecksumSha256 !== null)
        context.addIssue({
            code: "custom",
            path: ["sourcePdfChecksumSha256"],
            message: "A separately composed proof cannot claim a complete-book source checksum.",
        });
    if (value.sourcePdfPagePairs.some(([left, right]) => right !== left + 1))
        context.addIssue({
            code: "custom",
            path: ["sourcePdfPagePairs"],
            message: "Representative extracts must preserve consecutive page order.",
        });
    if (value.spreadWidthPoints !== value.trimWidthPoints * 2)
        context.addIssue({
            code: "custom",
            path: ["spreadWidthPoints"],
            message: "Review spread width must equal two trim pages.",
        });
    if (value.spreadHeightPoints !== value.trimHeightPoints)
        context.addIssue({
            code: "custom",
            path: ["spreadHeightPoints"],
            message: "Review spread height must equal trim height.",
        });
    const intentIds = value.intents.map(({ spreadId }) => spreadId);
    const renderedIds = value.renderedSpreads.map(({ spreadId }) => spreadId);
    if (intentIds.join("|") !== renderedIds.join("|"))
        context.addIssue({
            code: "custom",
            path: ["renderedSpreads"],
            message: "Rendered spread order must match the approved intent order.",
        });
    if (new Set(value.renderedSpreads.map(({ masterId }) => masterId)).size !== 3)
        context.addIssue({
            code: "custom",
            path: ["renderedSpreads"],
            message: "All three representative spreads require distinct rendered masters.",
        });
    value.renderedSpreads.forEach((spread, spreadIndex) => {
        if (spread.spreadWidthPoints !== value.spreadWidthPoints ||
            spread.spreadHeightPoints !== value.spreadHeightPoints)
            context.addIssue({
                code: "custom",
                path: ["renderedSpreads", spreadIndex],
                message: "Rendered spread dimensions must match the proof sheet dimensions.",
            });
        spread.regions.forEach((region, regionIndex) => {
            if (region.x + region.width > value.spreadWidthPoints ||
                region.y + region.height > value.spreadHeightPoints)
                context.addIssue({
                    code: "custom",
                    path: ["renderedSpreads", spreadIndex, "regions", regionIndex],
                    message: "A rendered region exceeds the spread boundary.",
                });
        });
    });
    if (value.compositionStatus === "PASS" &&
        value.renderedSpreads.some(({ overflowDetected, liveTypography, generatedPixels, colorOnlyMeaning }) => overflowDetected || !liveTypography || generatedPixels || colorOnlyMeaning))
        context.addIssue({
            code: "custom",
            path: ["compositionStatus"],
            message: "A passing composition cannot overflow, paint typography, generate pixels, or depend on color alone.",
        });
});
export const narrativeVisualQualityReportSchema = z
    .object({
    contract: z.literal("narrative-visual-quality/2"),
    product: narrativeBookProductSchema,
    benchmark: z.literal("Three Seeds for the Moon methodology, adapted for documentary memoir"),
    scope: z.enum(["FULL_BOOK", "REPRESENTATIVE_SPREADS"]),
    compositionPassed: z.boolean(),
    customerReady: z.boolean(),
    status: z.enum(["PASS", "BLOCKED", "COMPOSITION_PASS_MEDIA_BLOCKED"]),
    checks: z.array(z
        .object({
        id: z.string().trim().min(1).max(120),
        passed: z.boolean(),
        detail: z.string().trim().min(1).max(600),
    })
        .strict()),
    blockers: z.array(z.string().trim().min(1).max(400)).max(30),
})
    .strict()
    .superRefine((value, context) => {
    if (value.compositionPassed !== value.checks.every(({ passed }) => passed))
        context.addIssue({
            code: "custom",
            path: ["compositionPassed"],
            message: "Composition result must equal the conjunction of its checks.",
        });
    if (value.customerReady && (value.status !== "PASS" || value.blockers.length > 0))
        context.addIssue({
            code: "custom",
            path: ["customerReady"],
            message: "Customer-ready visual quality cannot retain blockers.",
        });
});
//# sourceMappingURL=narrative-visual-production.js.map